// ARCHIVO: src/pages/PerfilComercio.jsxn
// PÁGINA: Perfil del Comercio - Configuración completa del comercio
// FUNCIONALIDAD: Gestión de perfil, configuración de ventas, escáner, PWA, stock, backup

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Settings, 
  Store, 
  DollarSign, 
  Scan, 
  Smartphone, 
  Package, 
  FileText,
  Save,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  X,
  Printer,
  RotateCcw,
  Eye
} from 'lucide-react';
import '../styles/perfil-comercio.css';
// getTokenFromUrl removido - token manejado centralmente


const PerfilComercio = () => {
  // ESTADOS PRINCIPALES
  const [perfil, setPerfil] = useState({
    // ID DEL COMERCIO
    id: null,
    
    // DATOS BÁSICOS
    fantasia: '',
    empresa: '',
    contacto: '',
    direccion: '',
    ciudad: '',
    codpostal: '',
    telefono: '',
    whatsapp: '',
    email: '',
    cuit: '',
    condicion_iva: 'Responsable Inscripto',
    iibb: 'Exento',
    numero_iibb: '',
    jurisdiccion_iibb: 'Buenos Aires',
    
    // CONFIGURACIÓN DE VENTAS
    moneda_principal: 'ARS',
    formato_numeros: 'es_AR',
    redondeo_precios: 'centavo',
    porcentaje_iva: 21.00,
    aplicar_iva: true,
    
    
    // CONFIGURACIÓN DE IMPUESTOS ADICIONALES
    aplicar_iibb: false,
    porcentaje_iibb: 3.00,
    aplicar_ganancias: false,
    porcentaje_ganancias: 2.00,
    
    // CONFIGURACIÓN DE MÉTODOS DE PAGO
    metodos_pago_aceptados: ['efectivo', 'tarjeta_debito', 'tarjeta_credito', 'transferencia'],
    comision_efectivo: 0.00,
    comision_tarjeta_debito: 2.00,
    comision_tarjeta_credito: 5.00,
    comision_transferencia: 0.00,
    
    // CONFIGURACIÓN DE PLAZOS
    plazos_pago: ['contado', '30_dias', '60_dias'],
    plazo_predeterminado: 'contado',
    aplicar_intereses_plazo: false,
    porcentaje_interes_mensual: 2.00,
    
    // CONFIGURACIÓN DE TICKETS
    numero_ticket_inicio: 1,
    pie_ticket: '',
    impresora_predeterminada: '',
    logo_path: '',
    
    // CONFIGURACIÓN DE IMPRESORA
    tipo_impresora: 'termica_58mm',
    impresora_tickets: 'termica_58mm',
    impresora_informes: 'A4',
    margen_izquierdo: 10.00,
    margen_derecho: 10.00,
    margen_superior: 10.00,
    margen_inferior: 10.00,
    orientacion_papel: 'portrait',
    calidad_impresion: 'normal',
    
    // CONFIGURACIÓN DEL ESCÁNER
    delay_escaner: 5,
    sonido_escaner: true,
    modo_escaner: 'B&W',
    
    // CONFIGURACIÓN PWA
    nombre_app: '',
    notificaciones_push: true,
    
    
    // INTERNACIONALIZACIÓN
    idioma_principal: 'es',
    idiomas_soportados: ['es', 'en'],
    
    // LOGGING Y AUDITORÍA
    retencion_logs: 90,
    nivel_log: 'info',
    
    // HORARIOS DE ATENCIÓN - DOS TURNOS POR DÍA
    horarios_atencion: {
      lunes: { 
        abierto: true, 
        turno1: { inicio: '08:00', fin: '12:00' },
        turno2: { inicio: '14:00', fin: '18:00' }
      },
      martes: { 
        abierto: true, 
        turno1: { inicio: '08:00', fin: '12:00' },
        turno2: { inicio: '14:00', fin: '18:00' }
      },
      miercoles: { 
        abierto: true, 
        turno1: { inicio: '08:00', fin: '12:00' },
        turno2: { inicio: '14:00', fin: '18:00' }
      },
      jueves: { 
        abierto: true, 
        turno1: { inicio: '08:00', fin: '12:00' },
        turno2: { inicio: '14:00', fin: '18:00' }
      },
      viernes: { 
        abierto: true, 
        turno1: { inicio: '08:00', fin: '12:00' },
        turno2: { inicio: '14:00', fin: '18:00' }
      },
      sabado: { 
        abierto: true, 
        turno1: { inicio: '08:00', fin: '12:00' },
        turno2: { inicio: '14:00', fin: '17:00' }
      },
      domingo: { 
        abierto: false, 
        turno1: { inicio: '08:00', fin: '12:00' },
        turno2: { inicio: '14:00', fin: '18:00' }
      }
    }
  });

  const [loading, setLoading] = useState(false);
  const [seccionActiva, setSeccionActiva] = useState('perfil');
  const [modoEdicion, setModoEdicion] = useState(false);
  const [perfilOriginal, setPerfilOriginal] = useState({});
  const [tiposIva, setTiposIva] = useState([]);
  
  // ESTADOS PARA POPUP UNIFICADO
  const [popup, setPopup] = useState({
    isOpen: false,
    tipo: 'info',
    titulo: '',
    mensaje: '',
    detalles: null
  });


  useEffect(() => {
    const initializePerfil = async () => {
      // Esperar un poco para que se inicialice el sistema
      await new Promise(resolve => setTimeout(resolve, 500));
      
      try {
        await cargarPerfil();
        await cargarTiposIva();
      } catch (error) {
        console.error('❌ [PERFIL] Error al inicializar:', error);
      }
    };
    
    initializePerfil();
  }, []);

  // FUNCIONES HELPER PARA POPUP
  const mostrarPopup = useCallback((tipo, titulo, mensaje, detalles = null) => {
    setPopup({
      isOpen: true,
      tipo,
      titulo,
      mensaje,
      detalles
    });
  }, []);

  const cerrarPopup = useCallback(() => {
    setPopup(prev => ({ ...prev, isOpen: false }));
  }, []);

  // FUNCIÓN PARA CARGAR TIPOS DE IVA
  const cargarTiposIva = async () => {
    try {
      const response = await fetch('https://www.sinfilas.com.ar/sf3/ventas/api/tipos_iva.php');
      const result = await response.json();
      
      if (result.success) {
        setTiposIva(result.tipos_iva);
      } else {
        console.error('Error al cargar tipos de IVA:', result.message);
      }
    } catch (error) {
      console.error('Error al cargar tipos de IVA:', error);
    }
  };

  // FUNCIÓN PARA CARGAR EL PERFIL DESDE LA API
  const cargarPerfil = async () => {
    try {
      setLoading(true);
      
      // Obtener token de localStorage (manejado centralmente por App.jsx)
      const token = localStorage.getItem('comercio_token') || localStorage.getItem('token');
      
      // Si no hay token, intentar obtenerlo de la URL completa
        if (!token) {
          // Token no encontrado - usar fallback
          console.warn('Token no encontrado en localStorage');
        }
      
      if (!token) {
        console.error('❌ [PERFIL] No se encontró token en URL ni localStorage');
        mostrarPopup('error', 'Token No Encontrado', 'No se encontró el token de comercio. Por favor, accede nuevamente desde el enlace original.');
        return;
      }
      
      // Construir URL de la API
      const apiUrl = `https://www.sinfilas.com.ar/sf3/ventas/api/comercios_perfil_cargar.php?token=${token}`;
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      
      if (!response.ok) {
        throw new Error(`Error HTTP: ${response.status} - ${response.statusText}`);
      }
      
      const result = await response.json();
      if (result.success) {
        setPerfil(result.data);
        setPerfilOriginal(result.data); // Guardar copia original
      } else {
        mostrarPopup('error', 'Error al Cargar', result.message || 'No se pudo cargar el perfil del comercio');
      }
    } catch (error) {
      console.error('❌ [PERFIL] Error al cargar perfil:', error);
      
      // Verificar si es un error de token
      if (error.message.includes('Token') || error.message.includes('token')) {
        mostrarPopup('error', 'Error de Autenticación', 'El token de comercio no es válido. Por favor, accede nuevamente desde el enlace original.');
      } else if (error.message.includes('HTTP')) {
        mostrarPopup('error', 'Error de Servidor', 'Error del servidor al cargar el perfil. Intenta nuevamente.');
      } else {
        mostrarPopup('error', 'Error de Conexión', 'Error al cargar el perfil: ' + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // FUNCIÓN PARA VALIDAR CAMPOS REQUERIDOS
  const validarPerfil = () => {
    const errores = [];
    
    // Validar campos requeridos
    if (!perfil.fantasia?.trim()) {
      errores.push('El nombre de fantasía es requerido');
    }
    
    if (!perfil.empresa?.trim()) {
      errores.push('La razón social es requerida');
    }
    
    if (!perfil.email?.trim()) {
      errores.push('El email es requerido');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(perfil.email)) {
      errores.push('El email no tiene un formato válido');
    }
    
    if (!perfil.telefono?.trim()) {
      errores.push('El teléfono es requerido');
    }
    
    if (!perfil.direccion?.trim()) {
      errores.push('La dirección es requerida');
    }
    
    if (!perfil.ciudad?.trim()) {
      errores.push('La ciudad es requerida');
    }
    
    // Validar CUIT si está presente
    if (perfil.cuit?.trim() && !/^\d{2}-\d{8}-\d{1}$/.test(perfil.cuit)) {
      errores.push('El CUIT debe tener el formato XX-XXXXXXXX-X');
    }
    
    // Validar porcentajes
    if (perfil.porcentaje_iva < 0 || perfil.porcentaje_iva > 100) {
      errores.push('El porcentaje de IVA debe estar entre 0 y 100');
    }
    
    if (perfil.descuento_maximo < 0 || perfil.descuento_maximo > 100) {
      errores.push('El descuento máximo debe estar entre 0 y 100');
    }
    
    if (perfil.recargo_maximo < 0 || perfil.recargo_maximo > 100) {
      errores.push('El recargo máximo debe estar entre 0 y 100');
    }
    
    return errores;
  };

  // FUNCIÓN PARA GUARDAR EL PERFIL
  const guardarPerfil = async () => {
    try {
      setLoading(true);
      
      // Validar campos
      const errores = validarPerfil();
      if (errores.length > 0) {
        mostrarPopup('error', 'Errores de Validación', 'Por favor corrige los siguientes errores:', errores.join('\n• '));
        return;
      }
      
      // Obtener token de localStorage (manejado centralmente por App.jsx)
      const token = localStorage.getItem('comercio_token') || localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No se encontró token de comercio');
      }
      
      const response = await fetch(`https://www.sinfilas.com.ar/sf3/ventas/api/comercios_perfil_guardar.php?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(perfil)
      });
      
      const result = await response.json();
      
      if (result.success) {
        setPerfilOriginal(perfil); // Actualizar perfil original
        setModoEdicion(false); // Salir del modo edición
        mostrarPopup('success', 'Perfil Guardado', result.message || 'Los cambios se guardaron correctamente');
      } else {
        mostrarPopup('error', 'Error al Guardar', result.message || 'No se pudieron guardar los cambios');
      }
    } catch (error) {
      mostrarPopup('error', 'Error de Conexión', 'Error al guardar el perfil: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // FUNCIÓN PARA ACTIVAR MODO EDICIÓN
  const activarEdicion = () => {
    setModoEdicion(true);
  };

  // FUNCIÓN PARA CANCELAR EDICIÓN
  const cancelarEdicion = () => {
    setPerfil(perfilOriginal); // Restaurar datos originales
    setModoEdicion(false);
    mostrarPopup('info', 'Edición Cancelada', 'Se restauraron los valores originales del perfil');
  };

  // FUNCIÓN PARA ACTUALIZAR CAMPOS DEL PERFIL
  const actualizarCampo = useCallback((campo, valor) => {
    setPerfil(prev => {
      // Solo actualizar si el valor realmente cambió
      if (prev[campo] === valor) return prev;
      return {
        ...prev,
        [campo]: valor
      };
    });
  }, []);

  // FUNCIÓN PARA MANEJAR CAMBIO DE LOGO
  const handleLogoChange = useCallback(async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setLoading(true);

      // Crear FormData para enviar el archivo
      const formData = new FormData();
      formData.append('logo', file);
      formData.append('comercio_id', perfil.id);

      // Subir logo a la API
      const response = await fetch('https://www.sinfilas.com.ar/sf3/ventas/api/comercio_logo_subir.php', {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (result.success) {
        // Actualizar el perfil con el nuevo logo
        setPerfil(prev => ({
          ...prev,
          logo_path: result.data.nombre_archivo
        }));
        
        mostrarPopup('success', 'Logo Subido', 'El logo se subió correctamente');
        
        // Recargar el perfil para obtener datos actualizados
        await cargarPerfil();
      } else {
        mostrarPopup('error', 'Error al Subir Logo', result.message || 'No se pudo subir el logo');
      }
    } catch (error) {
      console.error('Error al subir logo:', error);
      mostrarPopup('error', 'Error de Conexión', 'Error al subir el logo: ' + error.message);
    } finally {
      setLoading(false);
      // Limpiar el input de archivo
      event.target.value = '';
    }
  }, [perfil.id, cargarPerfil]);

  // FUNCIÓN PARA ACTUALIZAR HORARIOS
  const actualizarHorario = useCallback((dia, campo, valor, turno = null) => {
    setPerfil(prev => {
      const horarioActual = prev.horarios_atencion[dia] || {};
      
      // Si se está activando un día (abierto: true) y no tiene turnos, inicializarlos
      if (campo === 'abierto' && valor === true) {
        return {
          ...prev,
          horarios_atencion: {
            ...prev.horarios_atencion,
            [dia]: {
              abierto: true,
              turno1: horarioActual.turno1 || { inicio: '08:00', fin: '12:00' },
              turno2: horarioActual.turno2 || { inicio: '14:00', fin: '18:00' }
            }
          }
        };
      }
      
      // Si se está actualizando un turno específico
      if (turno && (campo === 'inicio' || campo === 'fin')) {
        return {
          ...prev,
          horarios_atencion: {
            ...prev.horarios_atencion,
            [dia]: {
              ...horarioActual,
              [turno]: {
                ...horarioActual[turno],
                [campo]: valor
              }
            }
          }
        };
      }
      
      // Actualización normal (para el campo abierto)
      return {
        ...prev,
        horarios_atencion: {
          ...prev.horarios_atencion,
          [dia]: {
            ...horarioActual,
            [campo]: valor
          }
        }
      };
    });
  }, []);


  // FUNCIÓN HELPER PARA ESTILOS DE CAMPOS
  const getCampoStyles = useCallback((modoEdicion) => {
    return modoEdicion 
      ? 'border-none focus:ring-0 focus:border-none bg-transparent' 
      : 'border-none bg-transparent text-gray-600 cursor-not-allowed';
  }, []);

  // COMPONENTE DE POPUP UNIFICADO
  const Popup = useCallback(({ isOpen, onClose, tipo, titulo, mensaje, detalles = null }) => {
    if (!isOpen) return null;
    
    const icono = tipo === 'success' ? '✅' : tipo === 'error' ? '❌' : tipo === 'warning' ? '⚠️' : 'ℹ️';
    const colorFondo = tipo === 'success' ? '#f0fdf4' : tipo === 'error' ? '#fef2f2' : tipo === 'warning' ? '#fffbeb' : '#eff6ff';
    const colorBorde = tipo === 'success' ? '#bbf7d0' : tipo === 'error' ? '#fecaca' : tipo === 'warning' ? '#fed7aa' : '#bfdbfe';
    const colorTexto = tipo === 'success' ? '#166534' : tipo === 'error' ? '#dc2626' : tipo === 'warning' ? '#ea580c' : '#1e40af';
    
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '0.5rem',
          padding: '1.5rem',
          width: '100%',
          maxWidth: '32rem',
          margin: '0 1rem',
          textAlign: 'center',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
        }}>
          
          {/* Header del popup */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '1rem', 
            borderBottom: '1px solid #e5e7eb', 
            paddingBottom: '0.75rem' 
          }}>
            <h2 style={{ 
              fontSize: '1.25rem', 
              fontWeight: 'bold', 
              color: '#111827', 
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <span style={{ fontSize: '1.5rem' }}>{icono}</span>
              {titulo}
            </h2>
            <button
              onClick={onClose}
              style={{ 
                fontSize: '1.5rem', 
                fontWeight: 'bold', 
                color: '#9ca3af', 
                cursor: 'pointer', 
                border: 'none', 
                background: 'none',
                padding: '0.25rem'
              }}
            >
              ✕
            </button>
          </div>

          {/* Contenido del popup */}
          <div style={{ marginBottom: '1.5rem' }}>
            <p style={{ 
              fontSize: '1rem', 
              color: '#374151', 
              margin: '0 0 1rem 0',
              lineHeight: '1.5'
            }}>
              {mensaje}
            </p>
            
            {detalles && (
              <div style={{ 
                backgroundColor: colorFondo, 
                padding: '1rem', 
                borderRadius: '0.5rem', 
                border: `1px solid ${colorBorde}`,
                textAlign: 'left'
              }}>
                <h3 style={{ 
                  fontSize: '0.875rem', 
                  fontWeight: '600', 
                  color: colorTexto, 
                  marginBottom: '0.5rem', 
                  marginTop: 0 
                }}>
                  Detalles:
                </h3>
                <div style={{ 
                  fontSize: '0.875rem', 
                  color: '#374151',
                  whiteSpace: 'pre-line'
                }}>
                  {detalles}
                </div>
              </div>
            )}
          </div>

          {/* Botón de acción */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            paddingTop: '0.75rem', 
            borderTop: '1px solid #e5e7eb' 
          }}>
            <button
              onClick={onClose}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: tipo === 'success' ? '#10b981' : tipo === 'error' ? '#ef4444' : tipo === 'warning' ? '#f59e0b' : '#2563eb',
                color: 'white',
                borderRadius: '0.5rem',
                fontWeight: '600',
                border: 'none',
                cursor: 'pointer',
                fontSize: '0.875rem',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
              }}
              onMouseOut={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              ✅ Entendido - Cerrar
            </button>
          </div>
        </div>
      </div>
    );
  }, []);

  // COMPONENTE DE INPUT OPTIMIZADO
  const InputField = useCallback(({ 
    label, 
    value, 
    onChange, 
    disabled, 
    placeholder, 
    type = "text",
    className = "",
    fieldName,
    step,
    min,
    max,
    options = []
  }) => {
    if (type === "select") {
      return (
        <div key={fieldName}>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {label}
          </label>
          <select
            value={value}
            onChange={onChange}
            disabled={disabled}
            className={`w-full px-3 py-2 font-semibold ${getCampoStyles(!disabled)} ${className}`}
          >
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      );
    }
    
    return (
      <div key={fieldName}>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
        </label>
        <input
          type={type}
          value={value}
          onChange={onChange}
          disabled={disabled}
          className={`w-full px-3 py-2 font-semibold ${getCampoStyles(!disabled)} ${className}`}
          placeholder={placeholder}
          step={step}
          min={min}
          max={max}
        />
      </div>
    );
  }, [getCampoStyles]);

  // COMPONENTE DE SELECT OPTIMIZADO
  const SelectField = useCallback(({ 
    label, 
    value, 
    onChange, 
    disabled, 
    options,
    fieldName
  }) => (
    <div key={fieldName}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>
      <select
        value={value}
        onChange={onChange}
        disabled={disabled}
        className={`w-full px-3 py-2 font-semibold ${getCampoStyles(!disabled)}`}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  ), [getCampoStyles]);

  // COMPONENTE DE CHECKBOX OPTIMIZADO
  const CheckboxField = useCallback(({ 
    label, 
    checked, 
    onChange, 
    disabled, 
    fieldName,
    description = ""
  }) => (
    <div key={fieldName} className="md:col-span-2">
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          className={`w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 ${
            !disabled ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'
          }`}
        />
        <span className="text-sm font-medium text-gray-700">{label}</span>
      </label>
      {description && (
        <p className="text-xs text-gray-500 mt-1 ml-6">{description}</p>
      )}
    </div>
  ), []);

  // COMPONENTE DE SECCIÓN
  const Seccion = useCallback(({ id, titulo, icono, children, activa }) => (
    <div className={`bg-white rounded-lg shadow-sm border ${activa ? 'border-blue-500' : 'border-gray-200'}`}>
      <div 
        className={`flex items-center gap-3 p-4 cursor-pointer ${activa ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
        onClick={() => setSeccionActiva(id)}
      >
        {icono}
        <h3 className="text-lg font-semibold text-gray-900">{titulo}</h3>
      </div>
      {activa && (
        <div className="p-6 border-t border-gray-200">
          {children}
        </div>
      )}
    </div>
  ), []);

    // Mostrar preload mientras carga
    if (loading) {
      return (
        <div className="preload-overlay">
          <div className="preload-container">
            <img 
              src="/sf3/ventas/logosinfilas.png" 
              alt="SinFilas" 
              className="preload-logo"
            />
            <div className="preload-spinner"></div>
            <div className="preload-text">Cargando perfil del comercio...</div>
          </div>
        </div>
      );
    }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* HEADER */}
      <div className="max-w-7xl mx-auto">
        <div className="mb-8 animate-slide-in">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Settings className="w-8 h-8 text-blue-600" />
            Perfil del Comercio
          </h1>
          <p className="text-gray-600 mt-2">
            Configura y personaliza tu comercio según tus necesidades
          </p>
        </div>

        {/* POPUP UNIFICADO */}
        <Popup 
          isOpen={popup.isOpen}
          onClose={cerrarPopup}
          tipo={popup.tipo}
          titulo={popup.titulo}
          mensaje={popup.mensaje}
          detalles={popup.detalles}
        />

                 {/* BOTONES DE ACCIÓN */}
         <div className="actions-container-left mb-6">
           {!modoEdicion ? (
             <button
               onClick={activarEdicion}
               className="btn btn-primary"
             >
               <Settings className="w-4 h-4" />
               Editar Perfil
             </button>
           ) : (
             <>
               <button
                 onClick={guardarPerfil}
                 disabled={loading}
                 className="btn btn-success"
               >
                 <Save className="w-4 h-4" />
                 {loading ? 'Guardando...' : 'Guardar Cambios'}
               </button>
               
               <button
                 onClick={cancelarEdicion}
                 disabled={loading}
                 className="btn btn-danger"
               >
                 <X className="w-4 h-4" />
                 Cancelar
               </button>
             </>
           )}
           
           <button
             onClick={cargarPerfil}
             disabled={loading}
             className="btn btn-secondary"
           >
             <RefreshCw className="w-4 h-4" />
             Recargar
           </button>
         </div>

        {/* SECCIONES DE CONFIGURACIÓN */}
        <div className="grid gap-6">
          {/* 1. PERFIL BÁSICO */}
          <Seccion 
            id="perfil" 
            titulo="Perfil Básico" 
            icono={<Store className="w-6 h-6 text-blue-600" />}
            activa={seccionActiva === 'perfil'}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                             <InputField
                 label="Nombre de Fantasía"
                 value={perfil.fantasia}
                 onChange={(e) => actualizarCampo('fantasia', e.target.value)}
                 disabled={!modoEdicion}
                 placeholder="Nombre comercial"
                 fieldName="fantasia"
               />
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Razón Social
                </label>
                                 <input
                   type="text"
                   value={perfil.empresa}
                   onChange={(e) => actualizarCampo('empresa', e.target.value)}
                   disabled={!modoEdicion}
                   className={`w-full px-3 py-2 border-none font-semibold ${
                     modoEdicion 
                       ? 'focus:ring-0 focus:border-none' 
                       : 'bg-transparent text-gray-600 cursor-not-allowed'
                   }`}
                   placeholder="Razón social legal"
                 />
              </div>
              
              {/* CAMPO DEL LOGO */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Logo del Comercio
                </label>
                
                {/* Vista previa del logo actual */}
                {perfil.logo_path && (
                  <div className="mb-3 p-3 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                    <div className="text-center">
                      <p className="text-xs text-gray-600 mb-2 font-medium">LOGO ACTUAL:</p>
                      <img 
                        src={`https://www.sinfilas.com.ar/sf3/ventas/images/comercios/${perfil.id}/${perfil.logo_path ? perfil.logo_path.split('/').pop() : ''}`}
                        alt="Logo actual"
                        className="w-16 h-16 object-contain mx-auto shadow-lg rounded-lg border border-gray-200"
                        onLoad={() => console.log('✅ Logo cargado correctamente')}
                        onError={(e) => {
                          console.error('❌ Error cargando logo:', e.target.src);
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  </div>
                )}
                
                
                {/* Indicador cuando no hay logo */}
                {!perfil.logo_path && (
                  <div className="mb-3 p-4 bg-yellow-50 rounded-lg border-2 border-dashed border-yellow-300 text-center">
                    <div className="text-yellow-600">
                      <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="text-sm font-medium">No hay logo configurado</p>
                      <p className="text-xs text-yellow-500">Sube una imagen para personalizar tu comercio</p>
                    </div>
                  </div>
                )}

                {/* Input de archivo */}
                <div className="relative">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                    disabled={!modoEdicion || loading}
                    className={`w-full px-3 py-2 border rounded-lg font-semibold ${
                      modoEdicion && !loading
                        ? 'focus:ring-2 focus:ring-blue-500' 
                        : 'bg-transparent text-gray-600 cursor-not-allowed'
                    }`}
                  />
                  {loading && (
                    <div className="absolute inset-0 bg-blue-50 bg-opacity-75 rounded-lg flex items-center justify-center">
                      <div className="flex items-center space-x-2 text-blue-600">
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        <span className="text-sm font-medium">Subiendo logo...</span>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="mt-2 p-2 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-xs text-blue-700 font-medium mb-1">
                    📋 Especificaciones del Logo:
                  </p>
                  <ul className="text-xs text-blue-600 space-y-1">
                    <li>• Tamaño máximo: 100x100 píxeles</li>
                    <li>• Formatos soportados: JPEG, PNG, GIF</li>
                    <li>• Se recomienda usar imágenes cuadradas</li>
                    <li>• El logo se mostrará en tickets e informes</li>
                  </ul>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contacto
                </label>
                                 <input
                   type="text"
                   value={perfil.contacto}
                   onChange={(e) => actualizarCampo('contacto', e.target.value)}
                   disabled={!modoEdicion}
                   className={`w-full px-3 py-2 border rounded-lg font-semibold ${getCampoStyles(modoEdicion)}`}
                   placeholder="Persona de contacto"
                 />
              </div>
              
                            <InputField
                label="CUIT"
                value={perfil.cuit}
                onChange={(e) => actualizarCampo('cuit', e.target.value)}
                disabled={!modoEdicion}
                placeholder="XX-XXXXXXXX-X"
                fieldName="cuit"
              />
              
              <SelectField
                label="Condición de IVA"
                value={perfil.condicion_iva}
                onChange={(e) => actualizarCampo('condicion_iva', e.target.value)}
                disabled={!modoEdicion}
                fieldName="condicion_iva"
                options={tiposIva.map(tipo => ({
                  value: tipo.tipoiva,
                  label: tipo.tipoiva
                }))}
              />
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Dirección
                </label>
                                 <input
                   type="text"
                   value={perfil.direccion}
                   onChange={(e) => actualizarCampo('direccion', e.target.value)}
                   disabled={!modoEdicion}
                   className={`w-full px-3 py-2 border rounded-lg font-semibold ${getCampoStyles(modoEdicion)}`}
                   placeholder="Dirección completa"
                 />
              </div>
              
              <InputField
                label="IIBB (Ingresos Brutos)"
                value={perfil.iibb}
                onChange={(e) => actualizarCampo('iibb', e.target.value)}
                disabled={!modoEdicion}
                placeholder="Exento"
                fieldName="iibb"
              />
              
              <InputField
                label="Número de IIBB"
                value={perfil.numero_iibb}
                onChange={(e) => actualizarCampo('numero_iibb', e.target.value)}
                disabled={!modoEdicion}
                placeholder="Número de inscripción"
                fieldName="numero_iibb"
              />
              
              <InputField
                label="Jurisdicción IIBB"
                value={perfil.jurisdiccion_iibb}
                onChange={(e) => actualizarCampo('jurisdiccion_iibb', e.target.value)}
                disabled={!modoEdicion}
                placeholder="Buenos Aires"
                fieldName="jurisdiccion_iibb"
              />
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ciudad
                </label>
                                 <input
                   type="text"
                   value={perfil.ciudad}
                   onChange={(e) => actualizarCampo('ciudad', e.target.value)}
                   disabled={!modoEdicion}
                   className={`w-full px-3 py-2 border rounded-lg font-semibold ${getCampoStyles(modoEdicion)}`}
                   placeholder="Ciudad"
                 />
              </div>
              
              <InputField
                label="Código Postal"
                value={perfil.codpostal}
                onChange={(e) => actualizarCampo('codpostal', e.target.value)}
                disabled={!modoEdicion}
                placeholder="1870"
                fieldName="codpostal"
              />
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Teléfono
                </label>
                                 <input
                   type="tel"
                   value={perfil.telefono}
                   onChange={(e) => actualizarCampo('telefono', e.target.value)}
                   disabled={!modoEdicion}
                   className={`w-full px-3 py-2 border rounded-lg font-semibold ${getCampoStyles(modoEdicion)}`}
                   placeholder="Teléfono"
                 />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  WhatsApp
                </label>
                                 <input
                   type="tel"
                   value={perfil.whatsapp}
                   onChange={(e) => actualizarCampo('whatsapp', e.target.value)}
                   disabled={!modoEdicion}
                   className={`w-full px-3 py-2 border rounded-lg font-semibold ${getCampoStyles(modoEdicion)}`}
                   placeholder="WhatsApp"
                 />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                                 <input
                   type="email"
                   value={perfil.email}
                   onChange={(e) => actualizarCampo('email', e.target.value)}
                   disabled={!modoEdicion}
                   className={`w-full px-3 py-2 border rounded-lg font-semibold ${getCampoStyles(modoEdicion)}`}
                   placeholder="Email"
                 />
              </div>
            </div>
          </Seccion>

          {/* 2. CONFIGURACIÓN DE VENTAS */}
          <Seccion 
            id="ventas" 
            titulo="Configuración de Ventas" 
            icono={<DollarSign className="w-6 h-6 text-green-600" />}
            activa={seccionActiva === 'ventas'}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              
              {/* CONFIGURACIÓN BÁSICA */}
              <InputField
                label="Moneda Principal"
                value={perfil.moneda_principal}
                onChange={(e) => actualizarCampo('moneda_principal', e.target.value)}
                disabled={!modoEdicion}
                fieldName="moneda_principal"
                type="select"
                options={[
                  { value: 'ARS', label: 'Peso Argentino (ARS)' },
                  { value: 'USD', label: 'Dólar Estadounidense (USD)' },
                  { value: 'EUR', label: 'Euro (EUR)' }
                ]}
              />
              
              <InputField
                label="Formato de Números"
                value={perfil.formato_numeros}
                onChange={(e) => actualizarCampo('formato_numeros', e.target.value)}
                disabled={!modoEdicion}
                fieldName="formato_numeros"
                type="select"
                options={[
                  { value: 'es_AR', label: 'Argentina (es-AR)' },
                  { value: 'en_US', label: 'Estados Unidos (en-US)' },
                  { value: 'es_ES', label: 'España (es-ES)' }
                ]}
              />
              
              <InputField
                label="Redondeo de Precios"
                value={perfil.redondeo_precios}
                onChange={(e) => actualizarCampo('redondeo_precios', e.target.value)}
                disabled={!modoEdicion}
                fieldName="redondeo_precios"
                type="select"
                options={[
                  { value: 'sin_redondeo', label: 'Sin redondeo' },
                  { value: 'centavo', label: 'Al centavo' },
                  { value: 'cinco_centavos', label: 'A 5 centavos' },
                  { value: 'diez_centavos', label: 'A 10 centavos' },
                  { value: 'peso', label: 'Al peso' }
                ]}
              />
              
              <InputField
                label="Porcentaje de IVA"
                value={perfil.porcentaje_iva}
                onChange={(e) => actualizarCampo('porcentaje_iva', parseFloat(e.target.value))}
                disabled={!modoEdicion}
                fieldName="porcentaje_iva"
                type="number"
                step="0.01"
                min="0"
                max="100"
              />
              
              <CheckboxField
                label="Aplicar IVA automáticamente en las ventas"
                checked={perfil.aplicar_iva}
                onChange={(e) => actualizarCampo('aplicar_iva', e.target.checked)}
                disabled={!modoEdicion}
                fieldName="aplicar_iva"
                description="El IVA se calculará automáticamente en todas las ventas"
              />
              
              {/* CONFIGURACIÓN DE TICKETS */}
              <div className="md:col-span-2">
                <h4 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">Configuración de Tickets</h4>
              </div>
              
              <InputField
                label="Número de Inicio de Tickets"
                value={perfil.numero_ticket_inicio}
                onChange={(e) => actualizarCampo('numero_ticket_inicio', parseInt(e.target.value))}
                disabled={!modoEdicion}
                fieldName="numero_ticket_inicio"
                type="number"
                min="1"
                description="Número desde el cual comenzarán los tickets de venta"
              />
              
              <InputField
                label="Pie del Ticket"
                value={perfil.pie_ticket}
                onChange={(e) => actualizarCampo('pie_ticket', e.target.value)}
                disabled={!modoEdicion}
                fieldName="pie_ticket"
                type="textarea"
                placeholder="Ej: ¡Gracias por su compra! - Visite nuestro sitio web"
                description="Texto que aparecerá al final de cada ticket"
              />
              
              
              {/* CONFIGURACIÓN DE IMPUESTOS ADICIONALES */}
              <div className="md:col-span-2">
                <h4 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">Impuestos Adicionales</h4>
              </div>
              
              <CheckboxField
                label="Aplicar IIBB (Ingresos Brutos)"
                checked={perfil.aplicar_iibb}
                onChange={(e) => actualizarCampo('aplicar_iibb', e.target.checked)}
                disabled={!modoEdicion}
                fieldName="aplicar_iibb"
                description="Aplica el impuesto de Ingresos Brutos a las ventas"
              />
              
              {perfil.aplicar_iibb && (
                <InputField
                  label="Porcentaje de IIBB (%)"
                  value={perfil.porcentaje_iibb}
                  onChange={(e) => actualizarCampo('porcentaje_iibb', parseFloat(e.target.value))}
                  disabled={!modoEdicion}
                  fieldName="porcentaje_iibb"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                />
              )}
              
              <CheckboxField
                label="Aplicar impuesto a las ganancias"
                checked={perfil.aplicar_ganancias}
                onChange={(e) => actualizarCampo('aplicar_ganancias', e.target.checked)}
                disabled={!modoEdicion}
                fieldName="aplicar_ganancias"
                description="Aplica el impuesto a las ganancias a las ventas"
              />
              
              {perfil.aplicar_ganancias && (
                <InputField
                  label="Porcentaje de ganancias (%)"
                  value={perfil.porcentaje_ganancias}
                  onChange={(e) => actualizarCampo('porcentaje_ganancias', parseFloat(e.target.value))}
                  disabled={!modoEdicion}
                  fieldName="porcentaje_ganancias"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                />
              )}
              
              {/* CONFIGURACIÓN DE MÉTODOS DE PAGO */}
              <div className="md:col-span-2">
                <h4 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">Métodos de Pago</h4>
              </div>
              
              <InputField
                label="Comisión por efectivo (%)"
                value={perfil.comision_efectivo}
                onChange={(e) => actualizarCampo('comision_efectivo', parseFloat(e.target.value))}
                disabled={!modoEdicion}
                fieldName="comision_efectivo"
                type="number"
                step="0.01"
                min="0"
                max="100"
              />
              
              <InputField
                label="Comisión por tarjeta de débito (%)"
                value={perfil.comision_tarjeta_debito}
                onChange={(e) => actualizarCampo('comision_tarjeta_debito', parseFloat(e.target.value))}
                disabled={!modoEdicion}
                fieldName="comision_tarjeta_debito"
                type="number"
                step="0.01"
                min="0"
                max="100"
              />
              
              <InputField
                label="Comisión por tarjeta de crédito (%)"
                value={perfil.comision_tarjeta_credito}
                onChange={(e) => actualizarCampo('comision_tarjeta_credito', parseFloat(e.target.value))}
                disabled={!modoEdicion}
                fieldName="comision_tarjeta_credito"
                type="number"
                step="0.01"
                min="0"
                max="100"
              />
              
              <InputField
                label="Comisión por transferencia (%)"
                value={perfil.comision_transferencia}
                onChange={(e) => actualizarCampo('comision_transferencia', parseFloat(e.target.value))}
                disabled={!modoEdicion}
                fieldName="comision_transferencia"
                type="number"
                step="0.01"
                min="0"
                max="100"
              />
              
              {/* CONFIGURACIÓN DE PLAZOS */}
              <div className="md:col-span-2">
                <h4 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">Plazos de Pago</h4>
              </div>
              
              <InputField
                label="Plazo predeterminado"
                value={perfil.plazo_predeterminado}
                onChange={(e) => actualizarCampo('plazo_predeterminado', e.target.value)}
                disabled={!modoEdicion}
                fieldName="plazo_predeterminado"
                type="select"
                options={[
                  { value: 'contado', label: 'Contado' },
                  { value: '30_dias', label: '30 días' },
                  { value: '60_dias', label: '60 días' }
                ]}
              />
              
              <CheckboxField
                label="Aplicar intereses por plazo de pago"
                checked={perfil.aplicar_intereses_plazo}
                onChange={(e) => actualizarCampo('aplicar_intereses_plazo', e.target.checked)}
                disabled={!modoEdicion}
                fieldName="aplicar_intereses_plazo"
                description="Se aplicarán intereses a las ventas con plazo de pago"
              />
              
              {perfil.aplicar_intereses_plazo && (
                <InputField
                  label="Interés mensual (%)"
                  value={perfil.porcentaje_interes_mensual}
                  onChange={(e) => actualizarCampo('porcentaje_interes_mensual', parseFloat(e.target.value))}
                  disabled={!modoEdicion}
                  fieldName="porcentaje_interes_mensual"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                />
              )}
              
            </div>
          </Seccion>

          {/* 4. CONFIGURACIÓN DE IMPRESORA */}
          <Seccion 
            id="impresora" 
            titulo="Configuración de Impresora" 
            icono={<Printer className="w-6 h-6 text-green-600" />}
            activa={seccionActiva === 'impresora'}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              
              {/* TIPO DE IMPRESORA GENERAL */}
              <InputField
                label="Tipo de Impresora General"
                value={perfil.tipo_impresora}
                onChange={(e) => actualizarCampo('tipo_impresora', e.target.value)}
                disabled={!modoEdicion}
                fieldName="tipo_impresora"
                type="select"
                options={[
                  { value: 'A4', label: 'Impresora A4' },
                  { value: 'termica_58mm', label: 'Térmica 58mm' },
                  { value: 'termica_80mm', label: 'Térmica 80mm' }
                ]}
              />
              
              {/* IMPRESORA PARA TICKETS */}
              <InputField
                label="Impresora para Tickets"
                value={perfil.impresora_tickets}
                onChange={(e) => actualizarCampo('impresora_tickets', e.target.value)}
                disabled={!modoEdicion}
                fieldName="impresora_tickets"
                type="select"
                options={[
                  { value: 'A4', label: 'Impresora A4' },
                  { value: 'termica_58mm', label: 'Térmica 58mm' },
                  { value: 'termica_80mm', label: 'Térmica 80mm' }
                ]}
              />
              
              {/* IMPRESORA PARA INFORMES */}
              <InputField
                label="Impresora para Informes"
                value={perfil.impresora_informes}
                onChange={(e) => actualizarCampo('impresora_informes', e.target.value)}
                disabled={!modoEdicion}
                fieldName="impresora_informes"
                type="select"
                options={[
                  { value: 'A4', label: 'Impresora A4' },
                  { value: 'termica_58mm', label: 'Térmica 58mm' },
                  { value: 'termica_80mm', label: 'Térmica 80mm' }
                ]}
              />
              
              {/* ORIENTACIÓN DEL PAPEL */}
              <InputField
                label="Orientación del Papel"
                value={perfil.orientacion_papel}
                onChange={(e) => actualizarCampo('orientacion_papel', e.target.value)}
                disabled={!modoEdicion}
                fieldName="orientacion_papel"
                type="select"
                options={[
                  { value: 'portrait', label: 'Vertical' },
                  { value: 'landscape', label: 'Horizontal' }
                ]}
              />
              
              {/* CALIDAD DE IMPRESIÓN */}
              <InputField
                label="Calidad de Impresión"
                value={perfil.calidad_impresion}
                onChange={(e) => actualizarCampo('calidad_impresion', e.target.value)}
                disabled={!modoEdicion}
                fieldName="calidad_impresion"
                type="select"
                options={[
                  { value: 'draft', label: 'Borrador' },
                  { value: 'normal', label: 'Normal' },
                  { value: 'high', label: 'Alta' }
                ]}
              />
              
              {/* MÁRGENES */}
              <InputField
                label="Margen Izquierdo (mm)"
                value={perfil.margen_izquierdo}
                onChange={(e) => actualizarCampo('margen_izquierdo', parseFloat(e.target.value))}
                disabled={!modoEdicion}
                fieldName="margen_izquierdo"
                type="number"
                step="0.1"
                min="0"
                max="50"
              />
              
              <InputField
                label="Margen Derecho (mm)"
                value={perfil.margen_derecho}
                onChange={(e) => actualizarCampo('margen_derecho', parseFloat(e.target.value))}
                disabled={!modoEdicion}
                fieldName="margen_derecho"
                type="number"
                step="0.1"
                min="0"
                max="50"
              />
              
              <InputField
                label="Margen Superior (mm)"
                value={perfil.margen_superior}
                onChange={(e) => actualizarCampo('margen_superior', parseFloat(e.target.value))}
                disabled={!modoEdicion}
                fieldName="margen_superior"
                type="number"
                step="0.1"
                min="0"
                max="50"
              />
              
              <InputField
                label="Margen Inferior (mm)"
                value={perfil.margen_inferior}
                onChange={(e) => actualizarCampo('margen_inferior', parseFloat(e.target.value))}
                disabled={!modoEdicion}
                fieldName="margen_inferior"
                type="number"
                step="0.1"
                min="0"
                max="50"
              />
              
            </div>
          </Seccion>

          {/* 3. CONFIGURACIÓN DEL ESCÁNER */}
                     <Seccion 
             id="escaner" 
             titulo="Configuración del Escáner" 
             icono={<Scan className="w-6 h-6 text-purple-600" />}
             activa={seccionActiva === 'escaner'}
           >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Delay entre Escaneos (segundos)
                </label>
                                 <input
                   type="number"
                   min="1"
                   max="30"
                   value={perfil.delay_escaner}
                   onChange={(e) => actualizarCampo('delay_escaner', parseInt(e.target.value))}
                   disabled={!modoEdicion}
                   className={`w-full px-3 py-2 border rounded-lg font-semibold ${getCampoStyles(modoEdicion)}`}
                 />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Modo de Escaneo
                </label>
                                 <select
                   value={perfil.modo_escaner}
                   onChange={(e) => actualizarCampo('modo_escaner', e.target.value)}
                   disabled={!modoEdicion}
                   className={`w-full px-3 py-2 border rounded-lg font-semibold ${getCampoStyles(modoEdicion)}`}
                 >
                  <option value="B&W">Blanco y Negro</option>
                  <option value="color">Color</option>
                </select>
              </div>
              
              <div className="md:col-span-2">
                <label className="flex items-center gap-2">
                                     <input
                     type="checkbox"
                     checked={perfil.sonido_escaner}
                     onChange={(e) => actualizarCampo('sonido_escaner', e.target.checked)}
                     disabled={!modoEdicion}
                     className={`w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 ${
                       !modoEdicion ? 'opacity-50 cursor-not-allowed' : ''
                     }`}
                   />
                  <span className="text-sm font-medium text-gray-700">
                    Reproducir sonido al escanear productos
                  </span>
                </label>
              </div>
            </div>
          </Seccion>

          {/* 5. CONFIGURACIÓN PWA */}
          <Seccion 
            id="pwa" 
            titulo="Configuración PWA" 
            icono={<Smartphone className="w-6 h-6 text-indigo-600" />}
            activa={seccionActiva === 'pwa'}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre de la Aplicación
                </label>
                                 <input
                   type="text"
                   value={perfil.nombre_app}
                   onChange={(e) => actualizarCampo('nombre_app', e.target.value)}
                   disabled={!modoEdicion}
                   className={`w-full px-3 py-2 border rounded-lg font-semibold ${getCampoStyles(modoEdicion)}`}
                   placeholder="Nombre personalizado de la app"
                 />
              </div>
              
              <div className="md:col-span-2">
                <label className="flex items-center gap-2">
                                     <input
                     type="checkbox"
                     checked={perfil.notificaciones_push}
                     onChange={(e) => actualizarCampo('notificaciones_push', e.target.checked)}
                     disabled={!modoEdicion}
                     className={`w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 ${
                       !modoEdicion ? 'opacity-50 cursor-not-allowed' : ''
                     }`}
                   />
                  <span className="text-sm font-medium text-gray-700">
                    Habilitar notificaciones push
                  </span>
                </label>
              </div>
            </div>
          </Seccion>


          {/* 6. HORARIOS DE ATENCIÓN */}
          <Seccion 
            id="horarios" 
            titulo="Horarios de Atención" 
            icono={<FileText className="w-6 h-6 text-teal-600" />}
            activa={seccionActiva === 'horarios'}
          >
            <div className="grid gap-4">
              {Object.entries(perfil.horarios_atencion).map(([dia, config]) => (
                <div key={dia} className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center gap-4 mb-3">
                    <div className="w-24">
                      <span className="text-xl font-bold text-gray-900 capitalize nombre-dia">
                        {dia}
                      </span>
                    </div>
                    
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={config.abierto}
                        onChange={(e) => actualizarHorario(dia, 'abierto', e.target.checked)}
                        disabled={false}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                      />
                      <span className="text-sm text-gray-600">Abierto</span>
                    </label>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ml-28">
                    {/* TURNO 1 */}
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-gray-500 w-12">Turno 1:</span>
                      <input
                        type="time"
                        value={config.turno1?.inicio || '08:00'}
                        onChange={(e) => actualizarHorario(dia, 'inicio', e.target.value, 'turno1')}
                        disabled={!modoEdicion}
                        className={`px-2 py-1 text-sm border rounded ${
                          modoEdicion 
                            ? 'focus:ring-2 focus:ring-blue-500 focus:border-blue-500' 
                            : 'bg-gray-100 text-gray-600 cursor-not-allowed'
                        }`}
                      />
                      <span className="text-gray-500 text-sm">a</span>
                      <input
                        type="time"
                        value={config.turno1?.fin || '12:00'}
                        onChange={(e) => actualizarHorario(dia, 'fin', e.target.value, 'turno1')}
                        disabled={!modoEdicion}
                        className={`px-2 py-1 text-sm border rounded ${
                          modoEdicion 
                            ? 'focus:ring-2 focus:ring-blue-500 focus:border-blue-500' 
                            : 'bg-gray-100 text-gray-600 cursor-not-allowed'
                        }`}
                      />
                    </div>
                    
                    {/* TURNO 2 */}
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-gray-500 w-12">Turno 2:</span>
                      <input
                        type="time"
                        value={config.turno2?.inicio || '14:00'}
                        onChange={(e) => actualizarHorario(dia, 'inicio', e.target.value, 'turno2')}
                        disabled={!modoEdicion}
                        className={`px-2 py-1 text-sm border rounded ${
                          modoEdicion 
                            ? 'focus:ring-2 focus:ring-blue-500 focus:border-blue-500' 
                            : 'bg-gray-100 text-gray-600 cursor-not-allowed'
                        }`}
                      />
                      <span className="text-gray-500 text-sm">a</span>
                      <input
                        type="time"
                        value={config.turno2?.fin || '18:00'}
                        onChange={(e) => actualizarHorario(dia, 'fin', e.target.value, 'turno2')}
                        disabled={!modoEdicion}
                        className={`px-2 py-1 text-sm border rounded ${
                          modoEdicion 
                            ? 'focus:ring-2 focus:ring-blue-500 focus:border-blue-500' 
                            : 'bg-gray-100 text-gray-600 cursor-not-allowed'
                        }`}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Seccion>

        </div>

        {/* BOTONES DE ACCIÓN INFERIORES */}
        <div className="actions-container-left mt-8">
          {!modoEdicion ? (
            <button
              onClick={activarEdicion}
              className="btn btn-primary"
            >
              <Settings className="w-4 h-4" />
              Editar Perfil
            </button>
          ) : (
            <>
              <button
                onClick={guardarPerfil}
                disabled={loading}
                className="btn btn-success"
              >
                <Save className="w-4 h-4" />
                {loading ? 'Guardando...' : 'Guardar Cambios'}
              </button>
              
              <button
                onClick={cancelarEdicion}
                disabled={loading}
                className="btn btn-danger"
              >
                <X className="w-4 h-4" />
                Cancelar
              </button>
            </>
          )}
          
          <button
            onClick={cargarPerfil}
            disabled={loading}
            className="btn btn-secondary"
          >
            <RefreshCw className="w-4 h-4" />
            Recargar
          </button>
        </div>
      </div>
    </div>
  );
};

export default PerfilComercio;
